// src/pages/driverMgmtPage.js
import React, { useEffect, useState } from "react";
import { api } from "../scripts/api";
import Modal from "../components/Modal";
import { useNavigate } from "react-router-dom";

// Keyed by the server's computed `bucket` (same vocabulary as /driver/roster
// and the Drivers Today page). Fall back to the stale driver_status column
// only when the server didn't return a bucket (e.g. pre-upgrade backend).
const STATUS_COLORS = {
  available: { bg: '#d1fae5', fg: '#065f46', label: 'AVAILABLE' },
  on_move:   { bg: '#dbeafe', fg: '#1e40af', label: 'ON MOVE' },
  scheduled: { bg: '#fef3c7', fg: '#92400e', label: 'SCHEDULED' },
  off_today: { bg: '#f3f4f6', fg: '#6b7280', label: 'OFF TODAY' },
  off_duty:  { bg: '#f3f4f6', fg: '#6b7280', label: 'OFF DUTY' },
};

// Prefer the live bucket if the backend returned it; otherwise use the
// cached column so older backends still render something.
function statusKey(d) {
  return d.bucket || d.driver_status || 'off_duty';
}

export default function DriversPage() {
  const nav = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [editMode, setEditMode] = useState(false);
  // viewMode=true renders the driver details as copyable labels instead of
  // input boxes. Useful when dispatch is booking a flight / ordering a
  // permit and just needs to grab the passport # or plate. Toggled off via
  // the "✎ Edit" button in the modal header.
  const [viewMode, setViewMode] = useState(true);

  // Create-flow PIN options — when ticked (default) the server generates a
  // temp PIN and emails it to the driver with the welcome template.
  const [autoPin,          setAutoPin]          = useState(true);
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);

  // After the server issues a temp PIN we show the admin a confirmation
  // card so they can copy it to the driver if the email bounced. Also
  // covers the "resend welcome" flow on existing drivers.
  const [pinResult, setPinResult] = useState(null);
    // { temp_pin, welcome_email: { ok, reason? }, reset, email }
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [birthday, setBirthday] = useState("");
  const [passportNum, setPassportNum] = useState("");
  const [passportExp, setPassportExp] = useState("");
  const [dealerPlate, setDealerPlate] = useState("");
  const [dealerExp, setDealerExp] = useState("");
  const [currentLocation, setCurrentLocation] = useState("");
  const [driverStatus, setDriverStatus] = useState("available");
  const [homeTerminal, setHomeTerminal] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { loadDrivers(); }, []);

  const loadDrivers = async () => {
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.get("/driver");
      setDrivers(Array.isArray(data) ? data : []);
    } catch (e) {
      setMsg(e?.response?.data?.error || "Failed to load drivers.");
      setDrivers([]);
    } finally {
      setBusy(false);
    }
  };

  const createOrUpdateDriver = async () => {
    if (!fullName.trim()) { setMsg("Full name required."); return; }
    const payload = {
      FullName: fullName, Email: email, Phone: phone, Birthday: birthday,
      PassportNum: passportNum, PassportExp: passportExp,
      DealerPlate: dealerPlate, DealerExp: dealerExp,
      current_location: currentLocation, driver_status: driverStatus,
      home_terminal: homeTerminal || null,
    };

    // Create flow also wires up the portal PIN:
    //   - auto_pin + send_welcome_email default to true so a new driver
    //     gets a ready-to-use account on creation.
    //   - If admin didn't enter an email yet, silently skip the send so
    //     we don't fail the whole create.
    if (!editMode) {
      if (autoPin) {
        payload.auto_pin = true;
        payload.send_welcome_email = sendWelcomeEmail && !!email.trim();
        payload.force_pin_change   = true;
      }
    }

    setBusy(true); setMsg("");
    try {
      if (editMode) {
        await api.patch(`/driver/${selectedDriver.DriverId}`, payload);
        setMsg("Driver updated.");
        resetForm(); loadDrivers(); closeModal();
      } else {
        const { data } = await api.post("/driver", payload);
        if (data?.temp_pin) {
          // Show the temp PIN so the admin can copy/share it if the email
          // failed. Modal stays open until they dismiss this card.
          setPinResult({
            temp_pin:      data.temp_pin,
            welcome_email: data.welcome_email || null,
            reset:         false,
            email:         email.trim(),
            driverName:    fullName.trim(),
          });
          resetForm();
          await loadDrivers();
        } else {
          setMsg("Driver created.");
          resetForm(); loadDrivers(); closeModal();
        }
      }
    } catch (e) {
      setMsg(e?.response?.data?.error || "Operation failed.");
    } finally {
      setBusy(false);
    }
  };

  const deleteDriver = async (DriverId) => {
    if (!window.confirm("Delete this driver?")) return;
    setBusy(true);
    try {
      await api.delete(`/driver/${DriverId}`);
      setMsg("Driver deleted."); loadDrivers();
    } catch (e) {
      setMsg(e?.response?.data?.error || "Delete failed.");
    } finally { setBusy(false); }
  };

  const openCreateModal = () => {
    resetForm();
    setEditMode(false);
    setViewMode(false);          // create flow has nothing to view — jump straight to edit form
    setAutoPin(true);
    setSendWelcomeEmail(true);
    setPinResult(null);
    setSelectedDriver({});
  };

  const openEditModal = (driver) => {
    setFullName(driver.FullName || ""); setEmail(driver.Email || "");
    setPhone(driver.Phone || ""); setBirthday(driver.Birthday || "");
    setPassportNum(driver.PassportNum || ""); setPassportExp(driver.PassportExp || "");
    setDealerPlate(driver.DealerPlate || ""); setDealerExp(driver.DealerExp || "");
    setCurrentLocation(driver.current_location || "");
    setDriverStatus(driver.driver_status || "available");
    setHomeTerminal(driver.home_terminal || "");
    setEditMode(true);
    setViewMode(true);           // open in read-only "copy everything" mode by default
    setSelectedDriver(driver);
  };

  const closeModal = () => {
    setSelectedDriver(null);
    setEditMode(false);
    setViewMode(true);
    setPinResult(null);
  };

  const resetForm = () => {
    setFullName(""); setEmail(""); setPhone(""); setBirthday("");
    setPassportNum(""); setPassportExp(""); setDealerPlate(""); setDealerExp("");
    setCurrentLocation(""); setDriverStatus("available");
    setHomeTerminal("");
  };

  return (
    <div className="container">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, fontWeight: 900 }}>Driver Management</h2>
        <button className="btn-outline" onClick={loadDrivers} disabled={busy}>Refresh</button>
        <button className="btn-outline" onClick={() => nav("/admin/driver-calendar")}>Calendar</button>
        <button className="btn" onClick={openCreateModal} disabled={busy}>Add Driver</button>
      </div>

      {msg && <div className="muted" style={{ marginTop: 10 }}>{msg}</div>}

      {/* Unified driver grid — one compact card per driver, styled like the
          Drivers Today tiles, with all admin details (email, phone, plate,
          passport, expiries) + Edit/Delete inline. Replaces the previous
          two-section (Status Board + All Drivers) layout which duplicated
          the same information at two zoom levels. */}
      <div className="card" style={{ marginTop: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 10, flexWrap: "wrap" }}>
          <div style={{ fontWeight: 900 }}>Drivers ({drivers.length})</div>
        </div>
        {busy && <div className="muted">Loading…</div>}
        <div
          style={{
            display: "grid",
            // `min(100%, 260px)` lets the track shrink below 260px on a
            // narrow viewport (e.g. 390px iPhone with container padding)
            // instead of forcing horizontal overflow.
            gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 260px), 1fr))",
            gap: 12,
            alignItems: "stretch",
          }}
        >
          {drivers.map((d) => (
            <DriverAdminCard
              key={d.DriverId}
              driver={d}
              onEdit={() => openEditModal(d)}
              onDelete={() => deleteDriver(d.DriverId)}
              onSendWelcome={async () => {
                if (!d.Email) {
                  window.alert("This driver has no email on file. Add an email first.");
                  return;
                }
                const ok = window.confirm(
                  `Issue a new temporary PIN for ${d.FullName} and email it to ${d.Email} with portal instructions?\n\nThe driver will be forced to pick a new PIN on first login.`
                );
                if (!ok) return;
                try {
                  const { data } = await api.post(`/driver/${d.DriverId}/send-welcome`, { auto_pin: true });
                  setPinResult({
                    temp_pin:      data?.temp_pin,
                    welcome_email: data?.welcome_email || null,
                    reset:         true,
                    email:         d.Email,
                    driverName:    d.FullName,
                  });
                  await loadDrivers();
                } catch (e) {
                  window.alert(e?.response?.data?.error || "Failed to send welcome email.");
                }
              }}
            />
          ))}
          {!busy && drivers.length === 0 && <div className="muted">No drivers found.</div>}
        </div>
      </div>

      {selectedDriver && (
        <Modal onClose={closeModal}>
          <div className="row" style={{ justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
            <h2 style={{ margin: 0 }}>
              {editMode ? (viewMode ? "Driver Details" : "Edit Driver") : "Add Driver"}
            </h2>
            {editMode && (
              <button
                className={viewMode ? "btn" : "btn-outline"}
                onClick={() => setViewMode((v) => !v)}
                disabled={busy}
                title={viewMode ? "Switch to editable form" : "Return to quick-copy view"}
              >
                {viewMode ? "✎ Edit" : "👁 View"}
              </button>
            )}
          </div>

          {/* Quick-copy view: labeled values with per-field copy button. Used
              when dispatch is ordering permits / booking flights and needs
              to grab passport, phone, plate, etc. */}
          {editMode && viewMode ? (
            <div style={{ display: "grid", gap: 8 }}>
              <CopyField label="Full Name"        value={fullName} />
              <CopyField label="Email"            value={email} />
              <CopyField label="Phone"            value={phone} />
              <CopyField label="Birthday"         value={birthday} />
              <CopyField label="Passport #"       value={passportNum} />
              <CopyField label="Passport Expiry"  value={passportExp} />
              <CopyField label="Dealer Plate"     value={dealerPlate} />
              <CopyField label="Dealer Expiry"    value={dealerExp} />
              <CopyField label="Current Location" value={currentLocation} />
              <CopyField label="Home Terminal"    value={homeTerminal} />

              <div className="row" style={{ gap: 10, marginTop: 8 }}>
                <button className="btn-outline" onClick={closeModal}>Close</button>
              </div>
            </div>
          ) : (
            /* Editable form — original layout, shown during create and when
               the user clicks ✎ Edit on an existing driver. */
            <div style={{ display: "grid", gap: 10 }}>
              <input className="input" placeholder="Full Name *" value={fullName} onChange={(e) => setFullName(e.target.value)} />
              <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
                <input
                  className="input"
                  type="email"
                  inputMode="email"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <input
                  className="input"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="Phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
              <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
                <input className="input" placeholder="Birthday" value={birthday} onChange={(e) => setBirthday(e.target.value)} />
                <input className="input" placeholder="Passport #" value={passportNum} onChange={(e) => setPassportNum(e.target.value)} />
                <input className="input" placeholder="Passport Exp" value={passportExp} onChange={(e) => setPassportExp(e.target.value)} />
              </div>
              <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
                <input className="input" placeholder="Dealer Plate" value={dealerPlate} onChange={(e) => setDealerPlate(e.target.value)} />
                <input className="input" placeholder="Dealer Exp" value={dealerExp} onChange={(e) => setDealerExp(e.target.value)} />
              </div>
              <div style={{ borderTop: '1px solid #eee', paddingTop: 10 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>Status & Location</div>
                <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
                  <select className="input" value={driverStatus} onChange={(e) => setDriverStatus(e.target.value)}>
                    <option value="available">Available</option>
                    <option value="on_move">On Move</option>
                    <option value="off_duty">Off Duty</option>
                  </select>
                  <input className="input" placeholder="Current location (e.g. Calgary Yard)" value={currentLocation} onChange={(e) => setCurrentLocation(e.target.value)} />
                  <input
                    className="input"
                    placeholder="Home terminal (e.g. Calgary, Winnipeg)"
                    value={homeTerminal}
                    onChange={(e) => setHomeTerminal(e.target.value)}
                    title="Used to route moves to regionally-appropriate drivers"
                  />
                </div>
              </div>

              {/* Driver portal PIN — separate panel so it's never set by accident during a normal edit */}
              {editMode && selectedDriver?.DriverId && (
                <DriverPinPanel
                  driverId={selectedDriver.DriverId}
                  hasPin={!!selectedDriver.has_pin}
                  driverEmail={email}
                  onChanged={() => loadDrivers()}
                  onTempPin={(r) => setPinResult({ ...r, email, driverName: fullName })}
                />
              )}

              {/* Create flow: show the generate-PIN + email opts inline so
                  admins see exactly what will happen on submit. */}
              {!editMode && (
                <div style={{ border: "1px dashed rgba(22,163,74,0.5)", background: "rgba(22,163,74,0.06)", borderRadius: 8, padding: 10, display: "grid", gap: 8 }}>
                  <div style={{ fontWeight: 800, fontSize: 13, color: "#166534" }}>📩 Portal account</div>
                  <label className="row" style={{ gap: 8, alignItems: "flex-start", fontSize: 14 }}>
                    <input
                      type="checkbox"
                      checked={autoPin}
                      onChange={(e) => setAutoPin(e.target.checked)}
                    />
                    <span>
                      <div style={{ fontWeight: 700 }}>Generate temporary portal PIN</div>
                      <div className="muted" style={{ fontSize: 12 }}>
                        4-digit random PIN. Driver will be forced to pick their own on first login.
                      </div>
                    </span>
                  </label>
                  {autoPin && (
                    <label className="row" style={{ gap: 8, alignItems: "flex-start", fontSize: 14 }}>
                      <input
                        type="checkbox"
                        checked={sendWelcomeEmail && !!email.trim()}
                        disabled={!email.trim()}
                        onChange={(e) => setSendWelcomeEmail(e.target.checked)}
                      />
                      <span>
                        <div style={{ fontWeight: 700 }}>Email the PIN + "Add to Home Screen" instructions</div>
                        <div className="muted" style={{ fontSize: 12 }}>
                          {email.trim()
                            ? <>HTML welcome email sent to <strong>{email.trim()}</strong>.</>
                            : "Add a driver email above to enable this."}
                        </div>
                      </span>
                    </label>
                  )}
                </div>
              )}

              <div className="row" style={{ gap: 10 }}>
                <button
                  className="btn"
                  onClick={async () => {
                    await createOrUpdateDriver();
                    // On successful edit, drop back to view mode so the
                    // admin can immediately copy the updated values.
                    if (editMode) setViewMode(true);
                  }}
                  disabled={busy}
                >
                  {editMode ? "Update" : "Create"}
                </button>
                <button
                  className="btn-outline"
                  onClick={() => {
                    if (editMode) setViewMode(true); // return to quick-copy instead of closing
                    else closeModal();
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </Modal>
      )}

      {/* Temp-PIN result — shown after create / generate-and-email so the
          admin can copy the PIN to the driver manually if the email failed
          or the driver has no email on file. */}
      {pinResult && (
        <Modal onClose={() => { setPinResult(null); if (!editMode) closeModal(); }}>
          <h2 style={{ marginTop: 0 }}>
            {pinResult.reset ? "PIN reset" : "Driver portal PIN issued"}
          </h2>
          <div className="muted" style={{ fontSize: 14, marginBottom: 10 }}>
            {pinResult.driverName ? <>A temporary PIN has been issued for <strong>{pinResult.driverName}</strong>. </> : null}
            The driver will be required to pick a new PIN on first portal sign-in.
          </div>

          <div className="card" style={{ background: "#fef3c7", border: "1px solid #fde68a", padding: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.8, textTransform: "uppercase", color: "#92400e" }}>
              Temporary PIN
            </div>
            <div style={{
              fontFamily: "Consolas, 'Courier New', monospace",
              fontSize: 36, fontWeight: 900, letterSpacing: 8, color: "#7c2d12", marginTop: 6,
              userSelect: "all",
            }}>
              {pinResult.temp_pin}
            </div>
            <button
              className="btn-outline"
              style={{ marginTop: 10 }}
              onClick={() => navigator.clipboard?.writeText(String(pinResult.temp_pin || ""))}
            >
              📋 Copy PIN
            </button>
          </div>

          {pinResult.welcome_email && (
            <div style={{ marginTop: 12, fontSize: 13 }}>
              {pinResult.welcome_email.ok ? (
                <div style={{ color: "#166534" }}>
                  ✓ Welcome email sent{pinResult.email ? <> to <strong>{pinResult.email}</strong></> : null} with portal link + Add-to-Home-Screen instructions.
                </div>
              ) : (
                <div style={{ color: "#b45309" }}>
                  ⚠ Welcome email not sent ({pinResult.welcome_email.reason || "unknown"}).
                  Share the PIN above manually.
                </div>
              )}
            </div>
          )}

          <div className="row" style={{ justifyContent: "flex-end", marginTop: 14 }}>
            <button className="btn" onClick={() => { setPinResult(null); if (!editMode) closeModal(); }}>
              Done
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Driver portal PIN panel (embedded inside the driver edit modal) ────────
// Kept separate from the main form so PINs only change when the admin
// explicitly sets / clears them here. Never shows the current PIN (it's a
// bcrypt hash server-side).
function DriverPinPanel({ driverId, hasPin, driverEmail, onChanged, onTempPin }) {
  const [pin, setPin]     = useState("");
  const [confirm, setConfirm] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy]   = useState(false);
  const [msg, setMsg]     = useState("");
  const [err, setErr]     = useState("");

  const setOrChange = async () => {
    setErr(""); setMsg("");
    if (!/^\d{4,12}$/.test(pin)) return setErr("PIN must be 4–12 digits.");
    if (pin !== confirm)         return setErr("PINs don't match.");
    setBusy(true);
    try {
      const { data } = await api.patch(`/driver/${driverId}/pin`, {
        pin,
        send_welcome_email:    sendEmail && !!driverEmail,
        force_pin_change:      true,
      });
      setPin(""); setConfirm("");
      // Bubble the result up so the modal can show the "temp PIN + email
      // sent" confirmation card (handled by parent).
      onTempPin?.({ temp_pin: pin, welcome_email: data?.welcome_email || null, reset: hasPin });
      setMsg(hasPin ? "PIN updated." : "PIN set.");
      onChanged?.();
    } catch (e) {
      setErr(e?.response?.data?.error || "Failed to update PIN.");
    } finally { setBusy(false); }
  };

  // Generate a random PIN server-side + (by default) email it to the
  // driver along with "add to home screen" instructions. Marks the account
  // as must-change-pin so the driver picks their own on first sign-in.
  const generateAndEmail = async () => {
    setErr(""); setMsg("");
    if (sendEmail && !driverEmail) {
      setErr("Driver has no email on file — can't send the welcome email. Add an email first, or untick 'Send email' to generate the PIN without sending.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.patch(`/driver/${driverId}/pin`, {
        auto_pin:              true,
        send_welcome_email:    sendEmail,
        force_pin_change:      true,
      });
      onTempPin?.({
        temp_pin:      data?.temp_pin,
        welcome_email: data?.welcome_email || null,
        reset:         hasPin,
      });
      setMsg(hasPin ? "Temp PIN issued." : "Temp PIN set.");
      onChanged?.();
    } catch (e) {
      setErr(e?.response?.data?.error || "Failed to issue PIN.");
    } finally { setBusy(false); }
  };

  const clearPin = async () => {
    if (!window.confirm("Clear this driver's PIN? They won't be able to sign in to the portal until a new PIN is set.")) return;
    setErr(""); setMsg(""); setBusy(true);
    try {
      await api.patch(`/driver/${driverId}/pin`, { pin: "" });
      setMsg("PIN cleared.");
      onChanged?.();
    } catch (e) {
      setErr(e?.response?.data?.error || "Failed to clear PIN.");
    } finally { setBusy(false); }
  };

  return (
    <div
      style={{
        border: "1px solid var(--card-border)",
        borderRadius: 10,
        padding: 14,
        background: "rgba(127,127,127,0.04)",
        margin: "4px 0 8px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        <div>
          <div style={{ fontWeight: 900, fontSize: 14 }}>Driver Portal PIN</div>
          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
            {hasPin
              ? "✓ PIN is set. Issue a new temp PIN or type one manually below."
              : "No PIN yet. Driver can't log into the portal until one is set."}
          </div>
        </div>
        {hasPin && (
          <button className="btn-outline" onClick={clearPin} disabled={busy} style={{ color: "#dc2626", borderColor: "#fca5a5" }}>
            Clear PIN
          </button>
        )}
      </div>

      {/* One-click: generate + email. Preferred flow for new drivers. */}
      <div style={{ display: "grid", gap: 8, padding: 10, background: "rgba(22,163,74,0.06)", border: "1px dashed rgba(22,163,74,0.4)", borderRadius: 8, marginBottom: 12 }}>
        <div style={{ fontWeight: 800, fontSize: 13, color: "#166534" }}>
          📩 Generate temp PIN + email driver
        </div>
        <div className="muted" style={{ fontSize: 12, lineHeight: 1.5 }}>
          Creates a random 4-digit PIN{driverEmail ? (sendEmail ? <> and emails it to <strong>{driverEmail}</strong> with "Add to Home Screen" instructions.</> : <> (email off — you'll need to share it with them manually).</>) : <> — add an email to the driver first to send the welcome automatically.</>} They'll be forced to pick a permanent PIN on first sign-in.
        </div>
        <label className="row" style={{ gap: 8, alignItems: "center", fontSize: 13 }}>
          <input
            type="checkbox"
            checked={sendEmail && !!driverEmail}
            disabled={!driverEmail}
            onChange={(e) => setSendEmail(e.target.checked)}
          />
          <span>Email the PIN + portal instructions to the driver</span>
        </label>
        <button className="btn" onClick={generateAndEmail} disabled={busy} style={{ background: "#16a34a", alignSelf: "flex-start" }}>
          {busy ? "Working…" : (hasPin ? "🔄 Reset & Email PIN" : "✨ Generate & Email PIN")}
        </button>
      </div>

      {/* Manual entry — kept for the case where the driver already
          requested a specific PIN over the phone. */}
      <div className="muted" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", marginBottom: 6 }}>
        Or set manually
      </div>
      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        <input
          className="input"
          type="password"
          inputMode="numeric"
          maxLength={12}
          placeholder="New PIN (4–12 digits)"
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 12))}
          style={{ flex: "1 1 150px" }}
          autoComplete="new-password"
        />
        <input
          className="input"
          type="password"
          inputMode="numeric"
          maxLength={12}
          placeholder="Confirm PIN"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value.replace(/\D/g, "").slice(0, 12))}
          style={{ flex: "1 1 150px" }}
          autoComplete="new-password"
        />
        <button className="btn" onClick={setOrChange} disabled={busy || !pin}>
          {hasPin ? "Change PIN" : "Set PIN"}
        </button>
      </div>

      {err && <div style={{ marginTop: 8, color: "#dc2626", fontSize: 13, fontWeight: 700 }}>{err}</div>}
      {msg && <div style={{ marginTop: 8, color: "#15803d", fontSize: 13, fontWeight: 700 }}>{msg}</div>}
    </div>
  );
}

// ── Labelled value with one-click copy ────────────────────────────────────
// Used on the driver details modal so dispatch can grab passport, phone,
// plate etc. straight to the clipboard when booking flights or ordering
// permits. Clicking either the value or the 📋 button copies; the button
// briefly flashes "Copied" so the user knows it worked.
function CopyField({ label, value }) {
  const [copied, setCopied] = useState(false);
  const text = String(value ?? "").trim();
  const hasValue = text.length > 0;

  const copy = async () => {
    if (!hasValue) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch (_) {
      // Older browsers / insecure contexts — fall back to a hidden textarea.
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch (__) {}
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    <div
      onClick={copy}
      role={hasValue ? "button" : undefined}
      tabIndex={hasValue ? 0 : undefined}
      onKeyDown={(e) => { if (hasValue && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); copy(); } }}
      title={hasValue ? "Click to copy" : undefined}
      style={{
        display: "grid",
        gridTemplateColumns: "120px 1fr auto",
        alignItems: "center",
        gap: 10,
        padding: "8px 12px",
        border: "1px solid var(--card-border)",
        borderRadius: 8,
        background: copied ? "#dcfce7" : "var(--card-bg)",
        cursor: hasValue ? "pointer" : "default",
        transition: "background 120ms",
      }}
    >
      <div className="muted" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase" }}>
        {label}
      </div>
      <div
        style={{
          fontFamily: "Consolas, 'Courier New', monospace",
          fontSize: 14,
          fontWeight: 600,
          wordBreak: "break-all",
          color: hasValue ? "inherit" : "#9ca3af",
        }}
      >
        {hasValue ? text : "—"}
      </div>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); copy(); }}
        disabled={!hasValue}
        className="btn-outline"
        style={{
          padding: "4px 10px",
          fontSize: 12,
          minWidth: 78,
          color: copied ? "#166534" : undefined,
          borderColor: copied ? "#86efac" : undefined,
        }}
      >
        {copied ? "✓ Copied" : "📋 Copy"}
      </button>
    </div>
  );
}

// ── Compact driver card (admin view) ──────────────────────────────────────
// One tile per driver — shows live bucket, contact info, plate + passport
// expiries (with colour warnings when within 90 days), plus an embedded
// PIN panel and Edit/Delete actions. Mirrors the Drivers Today card layout
// so the two pages feel consistent.
function DriverAdminCard({ driver, onEdit, onDelete, onSendWelcome }) {
  const d = driver;
  const key = statusKey(d);
  const col = STATUS_COLORS[key] || STATUS_COLORS.off_duty;
  const label = col.label || String(key).toUpperCase();

  // Warn on expiries within 90 days (amber) or already past (red).
  const daysUntil = (s) => {
    if (!s) return null;
    const t = Date.parse(String(s));
    if (Number.isNaN(t)) return null;
    return Math.floor((t - Date.now()) / (1000 * 60 * 60 * 24));
  };
  const expiryStyle = (s) => {
    const n = daysUntil(s);
    if (n == null) return {};
    if (n < 0)  return { color: "#dc2626", fontWeight: 700 };
    if (n < 90) return { color: "#d97706", fontWeight: 700 };
    return {};
  };

  return (
    <div
      className="card"
      style={{
        padding: 12,
        background: "var(--card-bg)",
        border: "1px solid var(--card-border)",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      {/* Header: name + live status pill */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <div style={{ fontWeight: 800, fontSize: 15, minWidth: 0, wordBreak: "break-word" }}>
          {d.FullName || `Driver #${d.DriverId}`}
        </div>
        <span style={{
          fontSize: 10, fontWeight: 800, letterSpacing: 0.4, padding: "2px 8px",
          borderRadius: 99, background: col.bg, color: col.fg, whiteSpace: "nowrap",
        }}>
          {label}
        </span>
      </div>

      {/* Contact / identity block */}
      <div className="muted" style={{ fontSize: 12, display: "grid", gap: 2, lineHeight: 1.45 }}>
        {d.home_terminal && <div style={{ fontWeight: 700 }}>🏠 {d.home_terminal}</div>}
        {d.current_location && <div>📍 {d.current_location}</div>}
        {d.Phone && <div>📞 {d.Phone}</div>}
        {d.Email && <div style={{ wordBreak: "break-all" }}>✉️ {d.Email}</div>}
      </div>

      {/* Live context — mirrors the DriversTodayPage tile */}
      {d.active_move && (
        <div style={{ padding: 8, background: "#dbeafe", borderRadius: 6, color: "#1e40af" }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
            On move #{d.active_move.TripId}
          </div>
          <div style={{ fontSize: 12, marginTop: 2 }}>
            → {d.active_move.destination || "(no destination)"}
          </div>
        </div>
      )}
      {!d.active_move && d.next_scheduled && (
        <div style={{ padding: 8, background: "#ede9fe", borderRadius: 6, color: "#5b21b6" }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
            Next: move #{d.next_scheduled.TripId}
          </div>
          <div style={{ fontSize: 12, marginTop: 2 }}>
            {String(d.next_scheduled.md_start || "").slice(0, 10)}
            {d.next_scheduled.destination ? ` → ${d.next_scheduled.destination}` : ""}
          </div>
        </div>
      )}
      {!d.active_move && !d.next_scheduled && Array.isArray(d.unavailability) && d.unavailability.length > 0 && (
        <div style={{ padding: 8, background: "#fee2e2", borderRadius: 6, color: "#b91c1c" }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
            {d.unavailability[0].type === "holiday" ? "Holiday" : "Time off"}
          </div>
          <div style={{ fontSize: 12, marginTop: 2 }}>
            {d.unavailability[0].start_date} → {d.unavailability[0].end_date}
          </div>
        </div>
      )}

      {/* Admin-only details: plate, passport, PIN indicator */}
      <div style={{ fontSize: 12, display: "grid", gap: 2, lineHeight: 1.5, paddingTop: 6, borderTop: "1px dashed var(--card-border)" }}>
        <div>
          <span className="muted">Plate:</span>{" "}
          <strong>{d.DealerPlate || "—"}</strong>
          {d.DealerExp && (
            <span style={{ marginLeft: 6, fontSize: 11, ...expiryStyle(d.DealerExp) }}>
              exp {String(d.DealerExp).slice(0, 10)}
            </span>
          )}
        </div>
        <div>
          <span className="muted">Passport:</span>{" "}
          <strong>{d.PassportNum || "—"}</strong>
          {d.PassportExp && (
            <span style={{ marginLeft: 6, fontSize: 11, ...expiryStyle(d.PassportExp) }}>
              exp {String(d.PassportExp).slice(0, 10)}
            </span>
          )}
        </div>
        <div>
          <span className="muted">Portal PIN:</span>{" "}
          <strong style={{ color: d.has_pin ? "#16a34a" : "#dc2626" }}>
            {d.has_pin ? "Set" : "Not set"}
          </strong>
          {d.must_change_pin ? (
            <span style={{ marginLeft: 6, fontSize: 11, color: "#d97706", fontWeight: 700 }}>
              (temp — change required)
            </span>
          ) : null}
        </div>
      </div>

      {/* Actions */}
      <div className="row" style={{ gap: 6, marginTop: "auto", paddingTop: 4, flexWrap: "wrap" }}>
        <button className="btn-outline" style={{ flex: "1 1 80px", padding: "6px 10px", fontSize: 13 }} onClick={onEdit}>
          Edit
        </button>
        {onSendWelcome && (
          <button
            className="btn-outline"
            style={{ flex: "1 1 80px", padding: "6px 10px", fontSize: 13, color: "#166534", borderColor: "#86efac" }}
            onClick={onSendWelcome}
            title={d.Email ? "Generate a fresh temp PIN and email portal instructions" : "Driver has no email on file"}
          >
            📩 Welcome
          </button>
        )}
        <button
          className="btn-outline"
          style={{ flex: "1 1 80px", padding: "6px 10px", fontSize: 13, color: "#dc2626", borderColor: "#fca5a5" }}
          onClick={onDelete}
        >
          Delete
        </button>
      </div>
    </div>
  );
}